/**
 * How a friend's bubble moves on the Find radar between engine updates. Pure: the radar steps it once per animation
 * frame. A critically damped spring on (radius, angle) instead of a CSS transform transition: the transition restarted
 * from wherever it was on every update (a snap of direction mid-glide) and interpolated x/y in a straight line, so a
 * friend who swung from ahead to behind cut through the centre (Turnstile, 2026-10-14).
 */

import { BLE_IN_RANGE_PLACEHOLDER_METERS, type PeerPresenceKind } from './peerDisplay';
import {
  distanceToRadius,
  radarDirectionWeight,
  radarPlacementMeters,
  RADAR_CENTER,
  type RadarGeometryPeer,
} from './trackingRadarGeometry';

/** Spring stiffness in rad/s. Critically damped, it covers ~95% of a jump in 4.7/ω ≈ 0.5 s with no overshoot. */
export const RADAR_SPRING_OMEGA = 9.5;
/** A change of source (UWB / GPS / BLE) blends the two estimates over this long instead of jumping between them. */
export const RADAR_SOURCE_CROSSFADE_MS = 1000;
/** Arrow fade time constant: ~0.6 s to fade fully in or out. */
export const RADAR_ARROW_FADE_TAU_S = 0.2;
/** A bearing this trusted (see radarDirectionWeight) moves the bubble; below it the last good one is held. */
export const RADAR_BEARING_HOLD_WEIGHT = 0.5;
/** Where a friend with no bearing yet sits: 12 o'clock, as before. */
export const RADAR_NO_BEARING_ANGLE = -Math.PI / 2;

const TWO_PI = Math.PI * 2;

export type RadarMotionSource = 'uwb' | 'gps' | 'ble' | 'none';

export function motionSourceForKind(kind: PeerPresenceKind): RadarMotionSource {
  switch (kind) {
    case 'uwb-live':
      return 'uwb';
    case 'gps-live':
    case 'gps-stale':
      return 'gps';
    case 'ble-in-range':
      return 'ble';
    default:
      return 'none';
  }
}

/** Screen angle (radians, 0 = 3 o'clock, clockwise) of a compass bearing seen with the phone at a heading. */
export function screenAngleForBearing(bearingDeg: number, deviceHeadingDeg: number | null): number {
  const heading =
    deviceHeadingDeg !== null && Number.isFinite(deviceHeadingDeg) ? deviceHeadingDeg : 0;
  return ((bearingDeg - heading - 90) * Math.PI) / 180;
}

/** `target` moved by whole turns to sit within half a turn of `current`: the short way round. */
export function nearestEquivalentAngle(target: number, current: number): number {
  return target - TWO_PI * Math.round((target - current) / TWO_PI);
}

/** Exact step of a critically damped spring, stable for any dt (a dropped frame or a background tab). */
export function stepCriticalSpring(
  x: number,
  v: number,
  target: number,
  dtS: number,
  omega = RADAR_SPRING_OMEGA,
): { x: number; v: number } {
  if (!(dtS > 0)) {
    return { x, v };
  }
  const d = x - target;
  const c = v + omega * d;
  const e = Math.exp(-omega * dtS);
  return { x: target + (d + c * dtS) * e, v: (v - omega * c * dtS) * e };
}

function easeInOut(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

/** Where the engine says a peer is this frame, in radar terms. */
export interface RadarMotionTarget {
  radius: number;
  /** Screen angle in radians; already held through low-trust bearings (see {@link RadarBearingHold}). */
  angle: number;
  source: RadarMotionSource;
  /** 0..1 target opacity of the direction arrow. */
  arrow: number;
}

export interface RadarPeerMotion {
  r: number;
  vr: number;
  /** Unwrapped angle: may run past ±π so the spring always turns the short way. */
  a: number;
  va: number;
  arrow: number;
  source: RadarMotionSource;
  /** The goal the spring chases, after any cross-fade. */
  goalR: number;
  goalA: number;
  fade: { fromR: number; fromA: number; startMs: number } | null;
}

export function createPeerMotion(target: RadarMotionTarget): RadarPeerMotion {
  return {
    r: target.radius,
    vr: 0,
    a: target.angle,
    va: 0,
    arrow: target.arrow,
    source: target.source,
    goalR: target.radius,
    goalA: target.angle,
    fade: null,
  };
}

/** Advance one peer by dt. Mutates and returns `m`. */
export function stepPeerMotion(
  m: RadarPeerMotion,
  target: RadarMotionTarget,
  nowMs: number,
  dtS: number,
): RadarPeerMotion {
  const targetA = nearestEquivalentAngle(target.angle, m.a);

  if (target.source !== m.source) {
    // Blend from where the old source had them, so UWB→GPS reads as one glide, not a jump the spring then chases.
    m.fade = { fromR: m.goalR, fromA: m.goalA, startMs: nowMs };
    m.source = target.source;
  }

  let goalR = target.radius;
  let goalA = targetA;
  if (m.fade) {
    const t = (nowMs - m.fade.startMs) / RADAR_SOURCE_CROSSFADE_MS;
    if (t >= 1) {
      m.fade = null;
    } else {
      const e = easeInOut(t);
      const fromA = nearestEquivalentAngle(m.fade.fromA, targetA);
      goalR = m.fade.fromR + (target.radius - m.fade.fromR) * e;
      goalA = fromA + (targetA - fromA) * e;
    }
  }
  m.goalR = goalR;
  m.goalA = goalA;

  const r = stepCriticalSpring(m.r, m.vr, goalR, dtS);
  const a = stepCriticalSpring(m.a, m.va, goalA, dtS);
  m.r = Math.max(0, r.x);
  m.vr = r.v;
  m.a = a.x;
  m.va = a.v;

  // Keep the stored angle near zero so it never grows without bound; the goal moves with it.
  if (Math.abs(m.a) > TWO_PI * 4) {
    const turns = TWO_PI * Math.round(m.a / TWO_PI);
    m.a -= turns;
    m.goalA -= turns;
    if (m.fade) {
      m.fade.fromA -= turns;
    }
  }

  if (dtS > 0) {
    m.arrow += (target.arrow - m.arrow) * (1 - Math.exp(-dtS / RADAR_ARROW_FADE_TAU_S));
  }
  return m;
}

/** True once nothing visible is still moving, so the frame loop can stop. */
export function isPeerMotionSettled(m: RadarPeerMotion, target: RadarMotionTarget): boolean {
  if (m.fade) {
    return false;
  }
  const da = Math.abs(nearestEquivalentAngle(target.angle, m.a) - m.a);
  return (
    Math.abs(m.r - target.radius) < 0.05 &&
    Math.abs(m.vr) < 0.05 &&
    da * Math.max(m.r, 1) < 0.05 &&
    Math.abs(m.va) * Math.max(m.r, 1) < 0.05 &&
    Math.abs(m.arrow - target.arrow) < 0.01
  );
}

export function motionScreenPosition(m: Pick<RadarPeerMotion, 'r' | 'a'>): {
  x: number;
  y: number;
} {
  return { x: RADAR_CENTER + m.r * Math.cos(m.a), y: RADAR_CENTER + m.r * Math.sin(m.a) };
}

/** A held bearing older than this is dropped: half a minute on, the friend has moved (2026-10-16 audit). */
export const RADAR_BEARING_HOLD_MS = 30_000;

/** The last trusted compass bearing for one peer, when it was last trusted, and where it came from. */
export interface RadarHeldBearing {
  bearing: number;
  at: number;
  source: 'uwb' | 'gps' | null;
}

/**
 * The last trusted compass bearing per peer. Held as a bearing, not a screen angle, so a friend whose direction we
 * no longer trust still turns with the phone. Also stops a null bearing snapping a friend to 12 o'clock.
 */
export type RadarBearingHold = Map<string, RadarHeldBearing>;

export interface ResolvedRadarTarget extends RadarMotionTarget {
  id: string;
  screenX: number;
  screenY: number;
  /** 0..1 trust in the bearing (radarDirectionWeight). */
  directionWeight: number;
  /** The compass bearing the bubble is aimed along (live or held); null when there is none. */
  bearingDeg: number | null;
  /** 'live' when the peer's own bearing is trusted, 'held' when an earlier one stands in, null with no bearing. */
  bearingFrom: 'live' | 'held' | null;
}

/**
 * A held bearing must not outlive what it described (2026-10-16 audit: a friend gone Bluetooth-only kept an old GPS
 * bearing forever, turning with the phone). Bluetooth gives no direction, and a UWB range with no UWB direction says
 * the old GPS one no longer applies.
 */
function bearingHoldInvalid(peer: RadarGeometryPeer): boolean {
  if (peer.presenceKind === 'ble-in-range' || peer.presenceKind === 'none') {
    return true;
  }
  return peer.presenceKind === 'uwb-live' && (peer.bearingSource ?? null) === null;
}

/**
 * Where to aim one peer's bubble. Updates `hold` with a trusted bearing; uses the held one otherwise, until it is
 * RADAR_BEARING_HOLD_MS old or the peer's source rules it out. `arrowAllowed` is the old show-arrow rule (tracked,
 * right kind, compass ready); the trust weight fades it.
 */
export function resolveRadarTarget(
  peer: RadarGeometryPeer,
  deviceHeadingDeg: number | null,
  hold: RadarBearingHold,
  arrowAllowed: boolean,
  nowMs: number = Date.now(),
): ResolvedRadarTarget {
  const radius = distanceToRadius(radarPlacementMeters(peer));
  const weight = radarDirectionWeight(peer);
  if (bearingHoldInvalid(peer)) {
    hold.delete(peer.id);
  }
  let bearing: number | null = null;
  let bearingFrom: 'live' | 'held' | null = null;
  if (
    peer.bearingDeg !== null &&
    Number.isFinite(peer.bearingDeg) &&
    weight >= RADAR_BEARING_HOLD_WEIGHT
  ) {
    bearing = peer.bearingDeg;
    bearingFrom = 'live';
    hold.set(peer.id, { bearing, at: nowMs, source: peer.bearingSource ?? null });
  } else {
    const held = hold.get(peer.id);
    if (held && nowMs - held.at <= RADAR_BEARING_HOLD_MS) {
      bearing = held.bearing;
      bearingFrom = 'held';
    } else if (held) {
      hold.delete(peer.id);
    }
  }
  const angle =
    bearing === null ? RADAR_NO_BEARING_ANGLE : screenAngleForBearing(bearing, deviceHeadingDeg);
  const safeRadius = Number.isFinite(radius)
    ? radius
    : distanceToRadius(BLE_IN_RANGE_PLACEHOLDER_METERS);
  return {
    id: peer.id,
    radius: safeRadius,
    angle,
    source: motionSourceForKind(peer.presenceKind),
    // No bearing, live or held: no arrow. A low-trust bearing with nothing held left a faint arrow pointing at
    // 12 o'clock, a direction nobody measured (2026-10-16 audit).
    arrow: arrowAllowed && bearing !== null ? weight : 0,
    directionWeight: weight,
    bearingDeg: bearing,
    bearingFrom,
    screenX: RADAR_CENTER + safeRadius * Math.cos(angle),
    screenY: RADAR_CENTER + safeRadius * Math.sin(angle),
  };
}
